import { withRetry } from './retry';
import { useUsage } from '../stores/useUsage';
import { makeAttemptSignal } from './abortHelpers';
import { ttsSynthesize, ttsVoices } from '../lib/syncClient';

export type SynthesizeOptions = {
  /** Blob key the server should persist the audio under (typically the story id). */
  key: string;
  voiceId: string;
  modelId: string;
  text: string;
  signal?: AbortSignal;
  /** Hard ceiling per attempt, ms. Default 5 minutes. */
  timeoutMs?: number;
};

export type SynthesizeResult = {
  key: string;
  size: number;
  mime: string;
};

export type ElevenVoice = {
  voice_id: string;
  name: string;
  category?: string;
  labels?: Record<string, string>;
  preview_url?: string;
  high_quality_base_model_ids?: string[];
  verified_languages?: { model_id?: string; language?: string }[];
};

export function isVoiceCompatibleWith(
  voice: ElevenVoice,
  modelId: string
): boolean {
  if (voice.high_quality_base_model_ids?.includes(modelId)) return true;
  if (voice.verified_languages?.some((l) => l.model_id === modelId)) return true;
  return false;
}

const DEFAULT_TTS_TIMEOUT_MS = 300_000;

// Concurrency caps (kept client-side to mirror prior behaviour). v3 is slow
// enough that running >1 in parallel triggers upstream queueing on
// ElevenLabs' end.
class Semaphore {
  private active = 0;
  private queue: Array<() => void> = [];
  constructor(private readonly capacity: number) {}
  async acquire(): Promise<void> {
    if (this.active < this.capacity) {
      this.active++;
      return;
    }
    await new Promise<void>((resolve) => this.queue.push(resolve));
  }
  release(): void {
    const next = this.queue.shift();
    if (next) next();
    else this.active--;
  }
}

const v3Sem = new Semaphore(1);
const fastSem = new Semaphore(3);
const semFor = (modelId: string): Semaphore =>
  modelId === 'eleven_v3' ? v3Sem : fastSem;

export async function synthesize(
  opts: SynthesizeOptions
): Promise<SynthesizeResult> {
  return withRetry(
    async () => {
      const sem = semFor(opts.modelId);
      const queuedAt = Date.now();
      await sem.acquire();
      const queuedMs = Date.now() - queuedAt;
      if (queuedMs > 200) {
        console.info(`[tts] waited ${queuedMs}ms for ${opts.modelId} slot`);
      }
      const ctl = makeAttemptSignal(
        opts.signal,
        opts.timeoutMs ?? DEFAULT_TTS_TIMEOUT_MS,
        'tts'
      );
      try {
        const isV3 = opts.modelId === 'eleven_v3';
        const voice_settings = isV3
          ? {
              stability: 0.5,
              similarity_boost: 0.75,
              use_speaker_boost: true,
            }
          : {
              stability: 0.7,
              similarity_boost: 0.75,
              style: 0.3,
              use_speaker_boost: true,
            };
        const tagMatches = opts.text.match(/\[[a-z][a-z\s]*\]/gi) ?? [];
        const breakMatches = opts.text.match(/<break[^>]*>/gi) ?? [];
        console.info(
          `[tts] POST voice=${opts.voiceId.slice(0, 6)} model=${opts.modelId} ` +
            `chars=${opts.text.length} tags=${tagMatches.length} breaks=${breakMatches.length} ` +
            `settings=${JSON.stringify(voice_settings)}`
        );
        const startedAt = Date.now();
        const res = await ttsSynthesize(
          {
            key: opts.key,
            voice_id: opts.voiceId,
            model_id: opts.modelId,
            text: opts.text,
            voice_settings,
          },
          ctl.signal
        );
        try {
          useUsage.getState().recordEleven(opts.modelId, opts.text.length);
        } catch (e) {
          console.warn('usage record failed (eleven)', e);
        }
        console.info(
          `[tts] done in ${Date.now() - startedAt}ms (${res.size} bytes)`
        );
        return {
          key: res.key,
          size: res.size,
          mime: res.mime ?? 'audio/mpeg',
        };
      } catch (e) {
        if (ctl.isOurAbort()) {
          throw new DOMException(
            (e as Error)?.message ?? 'tts timed out',
            'TimeoutError'
          );
        }
        throw e;
      } finally {
        ctl.cleanup();
        sem.release();
      }
    },
    { signal: opts.signal, label: 'tts' }
  );
}

export async function listVoices(signal?: AbortSignal): Promise<ElevenVoice[]> {
  const data = await ttsVoices(signal);
  return (data.voices ?? []) as ElevenVoice[];
}
